import type { MyAccess } from '@/lib/api/types';

/**
 * The pure half of `AccessProvider` (OPERATOR_ACCESS.md section 5): the
 * shape every screen falls back to, the message a blocked entry point
 * shows, and the one check every gate runs.
 */

/**
 * `GET /capture/me` fails open. A transient error, or the read still being
 * in flight, never locks an operator out of capturing: `AccessProvider`
 * starts and falls back at this shape, so a flaky connection in the field
 * costs at most a stale answer, never a dead shutter. `reviewCaptures`
 * stays off, so a genuine reviewer's rights are never granted by an error
 * that is not theirs. The capture API enforces the real rights on every
 * route regardless of what this value says.
 */
export const FALLBACK_ACCESS: MyAccess = {
  role: 'client_user',
  permissions: {
    classify: true,
    enrich: true,
    descriptions: true,
    requestQuote: true,
    acceptQuote: true,
    apiAccess: true,
    captureInField: true,
    reviewCaptures: false,
  },
};

/** Shown at every capture entry point, with no shutter offered beside it,
 *  when the caller lacks `captureInField` (OPERATOR_ACCESS.md section 5). */
export const CANNOT_CAPTURE_MESSAGE = 'Your account cannot capture items. Ask your administrator.';

export function canCapture(access: MyAccess): boolean {
  return access.permissions.captureInField;
}
